import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { 
  Gift, 
  Save, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  Building,
  Coins,
  Ticket,
  ShieldAlert,
  Info
} from 'lucide-react';

const AdminRewards = () => {
  const [gyms, setGyms] = useState([]);
  const [selectedGymId, setSelectedGymId] = useState('');
  const [configs, setConfigs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [loadingGyms, setLoadingGyms] = useState(true);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch gyms for the dropdown
  useEffect(() => {
    const fetchGyms = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_BASE_URL}/api/admin/gyms/list-lightweight`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch gyms list.');
        const data = await response.json();
        setGyms(data);
        if (data.length > 0) {
          setSelectedGymId(data[0].id);
        }
      } catch (err) {
        console.error(err);
        setMessage({ type: 'error', text: 'Error loading gyms. Please try again.' });
      } finally {
        setLoadingGyms(false);
      }
    };
    fetchGyms();
  }, []);

  // Fetch configs and consents when selectedGymId changes
  useEffect(() => {
    if (!selectedGymId) return;

    const fetchConfigsAndConsents = async () => {
      setLoadingConfigs(true);
      setMessage(null);
      try {
        const token = localStorage.getItem('admin_token');
        
        // Fetch reward configs
        const configsRes = await fetch(`${API_BASE_URL}/api/admin/gyms/${selectedGymId}/reward-configs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!configsRes.ok) throw new Error('Failed to fetch reward configs.');
        const configsData = await configsRes.json();
        setConfigs(configsData);

        // Fetch gym consents
        const consentsRes = await fetch(`${API_BASE_URL}/api/admin/gyms/${selectedGymId}/milestone-consents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!consentsRes.ok) throw new Error('Failed to fetch gym consents.');
        const consentsData = await consentsRes.json();
        setConsents(consentsData);
      } catch (err) {
        console.error(err);
        setMessage({ type: 'error', text: 'Error loading settings for the selected gym.' });
      } finally {
        setLoadingConfigs(false);
      }
    };

    fetchConfigsAndConsents();
  }, [selectedGymId]);

  const handleConfigChange = (index, field, value) => {
    const updated = [...configs];
    
    // Parse numeric fields to numbers
    if (field === 'coinAmount' || field === 'fallbackCoinAmount') {
      value = value === '' ? 0 : parseInt(value, 10);
    }
    
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setConfigs(updated);
  };

  const handleSave = async () => {
    setMessage(null);
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/gyms/${selectedGymId}/reward-configs`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configs)
      });

      if (!response.ok) {
        const errMsg = await response.text();
        throw new Error(errMsg || 'Failed to update reward configs.');
      }

      const updatedConfigs = await response.json();
      setConfigs(updatedConfigs);
      setMessage({ type: 'success', text: 'Milestone Reward Configuration saved successfully.' });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: err.message || 'Error saving configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const getConsentStatus = (milestoneDays) => {
    const consent = consents.find(c => c.milestoneDays === milestoneDays);
    if (!consent) return { status: 'Pending / Not Opted In', color: 'text-amber-500 bg-amber-500/10 border-amber-500/20' };
    return consent.hasOptedIn 
      ? { status: 'Opted In / Consented', color: 'text-[#6CD800] bg-[#6CD800]/10 border-[#6CD800]/20' }
      : { status: 'Declined / Not Opted In', color: 'text-red-400 bg-red-400/10 border-red-400/20' };
  };

  if (loadingGyms) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <Loader2 className="animate-spin text-[#6CD800]" size={36} />
        <p className="text-gray-400 text-sm">Loading Gyms and configurations...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#1E293B] pb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-wide font-bebas flex items-center gap-2">
            <Gift className="text-[#6CD800]" /> MILESTONE REWARDS CONFIGURATION
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Configure default and gym-specific rewards for 30, 60, and 100-day consistency milestones.
          </p>
        </div>

        {/* Gym Selector */}
        <div className="flex items-center gap-3 bg-[#0F172A] border border-[#1E293B] p-2.5 rounded-lg">
          <Building className="text-gray-400" size={18} />
          <span className="text-sm font-semibold text-gray-300">Gym:</span>
          <select
            value={selectedGymId}
            onChange={(e) => setSelectedGymId(e.target.value)}
            className="bg-[#1E293B] text-white text-sm border-0 rounded px-3 py-1.5 focus:ring-2 focus:ring-[#6CD800]/50 outline-none min-w-[200px]"
          >
            {gyms.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({g.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Alert Banner */}
      {message && (
        <div className={`p-4 rounded-lg border flex items-start gap-3 transition-all ${
          message.type === 'success' 
            ? 'bg-[#6CD800]/10 border-[#6CD800]/30 text-[#6CD800]' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.type === 'success' ? <CheckCircle2 className="shrink-0" /> : <AlertTriangle className="shrink-0" />}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {loadingConfigs ? (
        <div className="flex flex-col items-center justify-center h-80 space-y-4 bg-[#0F172A] rounded-xl border border-[#1E293B]">
          <Loader2 className="animate-spin text-[#6CD800]" size={32} />
          <p className="text-gray-400 text-sm">Loading reward configurations for selected gym...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {configs.map((config, index) => {
              const consentInfo = getConsentStatus(config.milestoneDays);
              return (
                <div 
                  key={config.id || config.milestoneDays} 
                  className={`bg-[#0F172A] rounded-xl border p-6 transition-all duration-300 ${
                    config.isActive ? 'border-[#1E293B] hover:border-gray-700' : 'border-gray-800/40 opacity-70'
                  }`}
                >
                  {/* Card Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1E293B] pb-4 mb-5">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-[#1E293B] flex items-center justify-center text-white border border-gray-800">
                        <span className="text-xl font-bold text-[#6CD800]">{config.milestoneDays}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          {config.milestoneDays}-Day Milestone Progress
                        </h3>
                        <p className="text-xs text-gray-400">
                          Triggered when a member completes {config.milestoneDays} consistent scans.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Active Status Switch */}
                      <label className="flex items-center cursor-pointer gap-2 select-none">
                        <span className="text-xs font-semibold text-gray-400">Status:</span>
                        <div className="relative">
                          <input 
                            type="checkbox" 
                            checked={config.isActive} 
                            onChange={(e) => handleConfigChange(index, 'isActive', e.target.checked)}
                            className="sr-only" 
                          />
                          <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${config.isActive ? 'bg-[#6CD800]' : 'bg-[#1E293B]'}`}></div>
                          <div className={`absolute left-0.5 top-0.5 bg-[#0B0F19] w-5 h-5 rounded-full transition-transform duration-200 ${config.isActive ? 'translate-x-5' : 'translate-x-0'}`}></div>
                        </div>
                        <span className={`text-xs font-bold ${config.isActive ? 'text-[#6CD800]' : 'text-gray-500'}`}>
                          {config.isActive ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                  </div>

                  {config.isActive && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      
                      {/* Left: Primary Reward Config */}
                      <div className="space-y-4">
                        <div className="bg-[#0B0F19] p-4 rounded-lg border border-gray-800">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4 flex items-center gap-1.5">
                            <Coins size={14} className="text-[#6CD800]" /> Primary Reward (Claimed if Gym Consents or No Consent Needed)
                          </h4>

                          <div className="space-y-4">
                            {/* Primary Reward Type */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-400 mb-1.5">Primary Reward Type</label>
                              <select
                                value={config.primaryRewardType}
                                onChange={(e) => handleConfigChange(index, 'primaryRewardType', parseInt(e.target.value, 10))}
                                className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                              >
                                <option value={0}>FitScan Coins (Coins)</option>
                                <option value={1}>Promo Code (Sponsorship Deal)</option>
                                <option value={2}>Gym Specific Benefit (Free Month / Discount)</option>
                              </select>
                            </div>

                            {/* Coin Configuration */}
                            {config.primaryRewardType === 0 && (
                              <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Coin Amount</label>
                                <input
                                  type="number"
                                  value={config.coinAmount}
                                  onChange={(e) => handleConfigChange(index, 'coinAmount', e.target.value)}
                                  placeholder="e.g. 150"
                                  className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                />
                              </div>
                            )}

                            {/* Promo Code Configuration */}
                            {(config.primaryRewardType === 1 || config.primaryRewardType === 2) && (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">Promo Type</label>
                                    <select
                                      value={config.promoSubType || 0}
                                      onChange={(e) => handleConfigChange(index, 'promoSubType', parseInt(e.target.value, 10))}
                                      className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                    >
                                      <option value={0}>Discount Percentage</option>
                                      <option value={1}>Fixed Value Discount</option>
                                      <option value={2}>Free Trial Membership</option>
                                      <option value={3}>Custom Benefit</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">Promo Value</label>
                                    <input
                                      type="text"
                                      value={config.promoValue || ''}
                                      onChange={(e) => handleConfigChange(index, 'promoValue', e.target.value)}
                                      placeholder="e.g. 20% OFF or 1 Month Free"
                                      className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Promo Description</label>
                                  <input
                                    type="text"
                                    value={config.promoDescription || ''}
                                    onChange={(e) => handleConfigChange(index, 'promoDescription', e.target.value)}
                                    placeholder="e.g. 20% discount on next month membership"
                                    className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Consent Checkbox */}
                            <div className="pt-2 border-t border-gray-800 flex items-start gap-2.5">
                              <input
                                type="checkbox"
                                id={`consent-${config.milestoneDays}`}
                                checked={config.requiresGymConsent}
                                onChange={(e) => handleConfigChange(index, 'requiresGymConsent', e.target.checked)}
                                className="mt-1 rounded bg-[#1E293B] border-gray-700 text-[#6CD800] focus:ring-[#6CD800]/50 focus:ring-offset-0 focus:ring-1"
                              />
                              <div>
                                <label htmlFor={`consent-${config.milestoneDays}`} className="text-xs font-bold text-gray-300 cursor-pointer block select-none">
                                  Requires Gym Consent
                                </label>
                                <span className="text-[11px] text-gray-400 block mt-0.5">
                                  When checked, the Gym Owner must opt-in via their dashboard settings to offer this reward.
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Gym Consent Status & Fallback Reward Config */}
                      <div className="space-y-4">
                        {/* Gym Consent Card */}
                        {config.requiresGymConsent && (
                          <div className="bg-[#0B0F19] p-4 rounded-lg border border-gray-800">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-1.5">
                              <Building size={14} className="text-amber-500" /> Current Gym Owner Consent
                            </h4>

                            <div className={`p-3 rounded-lg border text-sm flex flex-col gap-1.5 ${consentInfo.color}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-xs uppercase tracking-wide">Status:</span>
                                <span className="font-bold text-xs">{consentInfo.status}</span>
                              </div>
                              <div className="text-[11px] leading-relaxed opacity-80 mt-1 flex items-start gap-1">
                                <Info size={11} className="mt-0.5 shrink-0" />
                                {consentInfo.status.includes('Opted In') 
                                  ? 'The gym owner has accepted this agreement. Members will receive the Primary Reward.' 
                                  : 'Gym owner has not consented yet. Members will receive the Fallback Reward configuration below instead.'
                                }
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Fallback Config (Only relevant if requiresGymConsent is active) */}
                        {config.requiresGymConsent ? (
                          <div className="bg-[#0B0F19] p-4 rounded-lg border border-gray-800">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4 flex items-center gap-1.5">
                              <ShieldAlert size={14} className="text-amber-500" /> Fallback Reward (Applied if Gym Opts Out)
                            </h4>

                            <div className="space-y-4">
                              {/* Fallback Reward Type */}
                              <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Fallback Reward Type</label>
                                <select
                                  value={config.fallbackRewardType ?? 0}
                                  onChange={(e) => handleConfigChange(index, 'fallbackRewardType', parseInt(e.target.value, 10))}
                                  className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                >
                                  <option value={0}>FitScan Coins</option>
                                  <option value={1}>Sponsor Promo Code</option>
                                </select>
                              </div>

                              {/* Fallback Coins */}
                              {config.fallbackRewardType === 0 && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Fallback Coin Amount</label>
                                  <input
                                    type="number"
                                    value={config.fallbackCoinAmount ?? 0}
                                    onChange={(e) => handleConfigChange(index, 'fallbackCoinAmount', e.target.value)}
                                    placeholder="e.g. 50"
                                    className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                  />
                                </div>
                              )}

                              {/* Fallback Promo */}
                              {config.fallbackRewardType === 1 && (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Fallback Promo Value</label>
                                  <input
                                    type="text"
                                    value={config.fallbackPromoValue || ''}
                                    onChange={(e) => handleConfigChange(index, 'fallbackPromoValue', e.target.value)}
                                    placeholder="e.g. 10% OFF Sponsor Deal"
                                    className="w-full bg-[#1E293B] text-white border border-[#1E293B] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="bg-[#0B0F19] p-6 rounded-lg border border-dashed border-gray-800 flex flex-col items-center justify-center text-center h-full min-h-[160px]">
                            <Ticket className="text-gray-600 mb-2" size={24} />
                            <p className="text-xs font-bold text-gray-400">No Consent Required</p>
                            <p className="text-[11px] text-gray-500 max-w-[280px] mt-1">
                              This milestone delivers the primary reward unconditionally. No fallback setup is required.
                            </p>
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                  {!config.isActive && (
                    <div className="bg-[#0B0F19]/50 rounded-lg p-4 border border-gray-800/20 text-center">
                      <p className="text-xs text-gray-500 font-semibold">
                        This Milestone reward program is disabled. Toggle status at the top to configure rules.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1E293B]">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-[#6CD800] text-[#0B0F19] rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-[#5bc200] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Saving Changes...
                </>
              ) : (
                <>
                  <Save size={16} /> Save Reward Rules
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRewards;
